/**
 * Facebook Pixel tracking utilities
 */

import { getUTMForTracking } from './utmCapture'

type FBEventName = 'ViewContent' | 'InitiateCheckout' | 'Subscribe' | 'Contact' | 'Lead' | 'CompleteRegistration'

interface FBEventParams {
  value?: number
  currency?: string
  content_name?: string
  content_ids?: string[]
  content_type?: string
  num_items?: number
  [key: string]: unknown
}

/**
 * Track Facebook Pixel event
 * @param eventID - Optional deduplication ID shared with server-side CAPI
 */
export const trackFBEvent = (eventName: FBEventName, params?: FBEventParams, eventID?: string): void => {
  if (typeof window !== 'undefined' && window.fbq) {
    if (eventID) {
      window.fbq('track', eventName, params, { eventID })
    } else {
      window.fbq('track', eventName, params)
    }

    // Log in development for debugging
    if (import.meta.env.DEV) {
      console.log('[FB Pixel]', eventName, params, eventID ? `(eventID: ${eventID})` : '')
    }
  }
}

/**
 * Track "View Pricing" - when user clicks Upgrade to Pro button (navigation)
 */
export const trackViewPricing = (source: string, feature?: string): void => {
  trackFBEvent('ViewContent', {
    content_name: 'Pricing Page',
    content_type: 'product',
    content_ids: ['pricing-page'],
    source,
    feature,
    ...getUTMForTracking(),
  })
}

/**
 * Track "Initiate Checkout" - when user clicks Subscribe to Pro on pricing page
 */
export const trackInitiateCheckout = (
  priceType: 'monthly' | 'quarterly' | 'lifetime',
  amount: number,
  eventID?: string
): void => {
  trackFBEvent('InitiateCheckout', {
    value: amount,
    currency: 'USD',
    content_name: `Pro Subscription - ${priceType.charAt(0).toUpperCase() + priceType.slice(1)}`,
    content_ids: [`pro-${priceType}`],
    content_type: 'product',
    num_items: 1,
    ...getUTMForTracking(),
  }, eventID)
}

/**
 * Track successful subscription (Subscribe event - better for recurring payments than Purchase)
 */
export const trackSubscribe = (
  priceType: 'monthly' | 'quarterly' | 'lifetime',
  amount: number,
  subscriptionId?: string,
  eventID?: string
): void => {
  trackFBEvent('Subscribe', {
    value: amount,
    currency: 'USD',
    content_name: `Pro Subscription - ${priceType.charAt(0).toUpperCase() + priceType.slice(1)}`,
    content_ids: [`pro-${priceType}`],
    content_type: 'product',
    num_items: 1,
    subscription_id: subscriptionId,
    predicted_ltv: priceType === 'lifetime' ? amount : undefined,
    ...getUTMForTracking(),
  }, eventID)
}

/**
 * Track contact attempt - when user clicks email link
 */
export const trackContact = (source: string = 'footer'): void => {
  trackFBEvent('Contact', {
    content_name: 'Email Contact',
    source,
    ...getUTMForTracking(),
  })
}

/**
 * Track registration completion - called on first sign-in
 * @param eventID - Optional dedup ID shared with server-side CAPI
 */
export const trackCompleteRegistration = (
  method: 'email_otp' | 'google' | 'facebook' | 'apple',
  eventID?: string
): void => {
  trackFBEvent('CompleteRegistration', {
    content_name: 'User Registration',
    registration_method: method,
    ...getUTMForTracking(),
  }, eventID)
}

/**
 * Track AI Chat session start
 */
export const trackAIChatStart = (applicationId: string, hasJobContent: boolean): void => {
  trackFBEvent('ViewContent', {
    content_name: 'AI Assistant',
    content_type: 'feature',
    content_ids: [applicationId],
    has_job_content: hasJobContent,
    ...getUTMForTracking(),
  })
}

/**
 * Track AI Chat message sent
 */
export const trackAIChatMessage = (applicationId: string, messageCount: number): void => {
  trackFBEvent('Lead', {
    content_name: 'AI Assistant Message',
    content_ids: [applicationId],
    message_count: messageCount,
    ...getUTMForTracking(),
  })
}

/**
 * Track application added - fires Lead event on first application (activation)
 * @param isFirst - Whether this is the user's first application
 * @param totalCount - Total number of applications after adding this one
 */
export const trackApplicationAdded = (isFirst: boolean, totalCount: number, eventID?: string): void => {
  // Only fire Lead event on activation (first application)
  if (isFirst) {
    trackFBEvent('Lead', {
      content_name: 'First Application Added',
      content_type: 'activation',
      value: 1,
      num_items: totalCount,
      ...getUTMForTracking(),
    }, eventID)
  }
}

/**
 * Track resume builder: a new resume was created
 */
export const trackResumeCreated = (
  source: 'blank' | 'import_analysis' | 'duplicate' | 'tailor',
  template?: string
): void => {
  trackFBEvent('ViewContent', {
    content_name: 'Resume Created',
    content_type: 'resume_builder',
    content_category: source,
    template,
    ...getUTMForTracking(),
  })
}

/**
 * Track resume builder: PDF download — a strong intent/conversion signal
 */
export const trackResumeDownloaded = (template: string, font?: string): void => {
  trackFBEvent('Lead', {
    content_name: 'Resume Downloaded',
    content_type: 'resume_builder',
    template,
    font,
    value: 1,
    ...getUTMForTracking(),
  })
}

/**
 * Track resume builder: AI tailor used (Pro feature, conversion intent)
 */
export const trackResumeTailored = (applicationId: string): void => {
  trackFBEvent('Lead', {
    content_name: 'Resume Tailored',
    content_type: 'resume_builder',
    content_ids: [applicationId],
    value: 1,
    ...getUTMForTracking(),
  })
}
